import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography, Stack, Button, Chip, IconButton, useMediaQuery, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MaterialTransportCard from './MaterialTransportCard';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EventIcon from '@mui/icons-material/Event';
import { Formik, Form } from 'formik';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import materialTransportService from '../../service/materialTransport.service';
import MaterialTransportFields from './MaterialTransportFields';
import Yup from '../../utils/yup';
import {
  MaterialTransportItemStatus,
  MaterialTransportOperation,
  MaterialTransportOperationList,
  MaterialTransportView,
} from '../../config/materialTransport';
import { generateAutocompleteItems } from '../../utils/helper';
import masterDataService from '../../service/masterData.service';
import MaterialTransportItemTable from './MaterialTransportItemTable';
import { ConfirmDialogUI, DatePickerUI, FileUploadUI, TabsUI } from '../Interface';
import colorFinder from '../../utils/colorFinder';
import { NotificationType } from '../../config';
import addNotification from '../../utils/addNotification';
import { sxConfig } from './materialTransport.style';
import MaterialTransportActions from './MaterialTransportActions';
import dayjs from 'dayjs';

export const MaterialTransportPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [rows, setRows] = useState([]);
  const [selectedValues, setSelectedValues] = useState({});
  const [initValues, setInitValues] = useState({});
  const [isAdding, setIsAdding] = useState(false);
  const [refetch, setRefetch] = useState(false);
  const [activeTab, setActiveTab] = useState(MaterialTransportView.PENDING);
  const [autocompleteData, setAutocompleteData] = useState({});
  const [autocompleteOptions, setAutoCompleteOptions] = useState({
    company: [],
    project: [],
    quantityUnit: [],
  });

  const defConfirmDialogData = {
    open: false,
    message: '',
    handleChangeElement: () => {},
  };
  const [mobileDetailsOpen, setMobileDetailsOpen] = useState(false);
  const [confirmDialogData, setConfirmDialogData] = useState(defConfirmDialogData);
  const [isOrderSaving, setIsOrderSaving] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [selectedDate, setSelectedDate] = useState(dayjs());

  const defTransportItem = {
    isNew: true,
    visOrder: null,
    itemCode: null,
    qty: null,
    qtyUnitId: null,
    status: MaterialTransportItemStatus.PENDING,
    diffQty: null,
  };

  const defaltValues = {
    visOrder: null,
    companyId: null,
    projectId: null,
    operation: MaterialTransportOperation.PICKUP,
    description: '',
    completed: false,
    plannedDate: dayjs(),
    materialTransportItem: [],
    materialTransportAttachment: [],
  };

  useEffect(() => {
    setInitValues(defaltValues);
    initDefaultValues();
  }, []);

  useEffect(() => {
    getTransports();
  }, [activeTab, selectedDate]);

  useEffect(() => {
    if (refetch) getTransports();
  }, [refetch]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      valuesToForm();
    } else {
      setInitValues({
        ...defaltValues,
        plannedDate: selectedDate,
      });
    }
  }, [selectedValues, rows.length, selectedDate]);

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .companyToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .quantityUnitToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          company: resp[0],
          project: resp[1],
          quantityUnit: resp[2],
        };
        setAutocompleteData(aData);

        const filteredProject = aData.project.filter((p) => p.active);

        const acOptions = {
          company: generateAutocompleteItems(aData.company, 'name', 'id'),
          project: generateAutocompleteItems(filteredProject, 'projectName', 'projectId'),
          quantityUnit: generateAutocompleteItems(aData.quantityUnit, 'code', 'id'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {
        //hideLoader();
      });
  };

  const getTransports = () => {
    const body = {
      type: activeTab,
      date: dayjs(selectedDate).format('YYYY-MM-DD'),
    };

    materialTransportService.getItems(body).then((data) => {
      setRows(data);
      setRefetch(false);

      if (!isAdding) {
        const selectedRow = data.find((row) => row.id === selectedValues?.id);

        setSelectedValues(selectedRow ?? (isMobile ? {} : data[0] ?? {}));

        if (isMobile && !selectedRow) {
          setMobileDetailsOpen(false);
        }
      }
    });
  };

  const validationSchema = Yup.object().shape({
    companyId: Yup.string().required(),
    projectId: Yup.string().required(),
    description: Yup.string().nullable(),
    completed: Yup.boolean(),
    plannedDate: Yup.string().required(),
    materialTransportItem: Yup.array().of(
      Yup.object().shape({
        itemCode: Yup.string().required(),
        qty: Yup.number().required(),
        qtyUnitId: Yup.string().required(),
        diffQty: Yup.number().nullable(),
      })
    ),
  });

  const valuesToForm = () => {
    const {
      id,
      companyId,
      projectId,
      description,
      visOrder,
      operation,
      materialTransportItem,
      materialTransportAttachment,
      completed,
      plannedDate,
    } = selectedValues;

    setInitValues({
      id,
      companyId,
      projectId,
      description,
      visOrder,
      operation,
      materialTransportAttachment,
      completed,
      plannedDate: dayjs(plannedDate),
      materialTransportItem: materialTransportItem?.map((i) => ({
        id: i.id,
        itemCode: i.itemCode,
        itemName: i.item.itemName,
        status: i.status,
        qty: i.qty,
        qtyUnitId: i.qtyUnitId,
        diffQty: i.diffQty,
      })),
    });
  };

  const handleSubmit = (values) => {
    if (values.id) {
      handleUpdate(values);
    } else {
      handleInsert(values);
    }
  };

  const handleInsert = (values) => {
    const insertObj = {
      ...values,
      plannedDate: dayjs(values.plannedDate).startOf('day').add(12, 'hours').toISOString(),
    };

    const formData = new FormData();
    formData.append('data', JSON.stringify(insertObj));

    if (insertObj.materialTransportAttachment) {
      for (const file of insertObj.materialTransportAttachment) {
        formData.append('files', file);
      }
    }

    materialTransportService.createItem(formData).then((data) => {
      addNotification({
        content: 'Sikereses mentés!',
        type: NotificationType.SUCCESS,
      });
      setSelectedValues(data);
      setIsAdding(false);
      setRefetch(true);
    });
  };

  const handleUpdate = (values) => {
    const updateObj = {
      ...values,
      plannedDate: dayjs(values.plannedDate).startOf('day').add(12, 'hours').toISOString(),
    };

    updateObj.existingFiles = updateObj.materialTransportAttachment?.map((f) => f.name);

    const formData = new FormData();
    formData.append('data', JSON.stringify(updateObj));

    if (updateObj.materialTransportAttachment) {
      for (const file of updateObj.materialTransportAttachment) {
        if (file instanceof File) {
          formData.append('files', file);
        }
      }
    }

    materialTransportService.updateItem(formData).then((data) => {
      addNotification({
        content: 'Sikereses mentés!',
        type: NotificationType.SUCCESS,
      });

      setSelectedValues(data);
      setRefetch(true);
    });
  };

  const handleAddClick = () => {
    setSelectedValues({});
    setIsAdding(true);
    setMobileDetailsOpen(true);
  };

  const handleBackToList = () => {
    setMobileDetailsOpen(false);
    setIsAdding(false);
    setSelectedValues({});
  };

  const handleClose = () => {
    setIsAdding(false);
    setSelectedValues(isMobile ? {} : rows[0] ?? {});
    setMobileDetailsOpen(false);
  };

  const getOperationData = (operationValue) => {
    const findedOperation = MaterialTransportOperationList.find((o) => o.value === operationValue);
    const operationColor = colorFinder(findedOperation?.color);

    return { findedOperation, operationColor };
  };

  const onCloseConfirmDialog = () => {
    setConfirmDialogData(defConfirmDialogData);
  };

  const handleRemoveElement = async (id) => {
    materialTransportService.deleteItem(id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetch(true);
      onCloseConfirmDialog();
      setSelectedValues({});
    });
  };

  const handleDelete = (id) => {
    setConfirmDialogData({
      open: true,
      message: 'Biztosan törölni szeretné?',
      handleChangeElement: () => handleRemoveElement(id),
    });
  };

  const selectedIndex = rows.findIndex((row) => row.id === selectedValues?.id);

  const currentOrder = isAdding ? rows.length + 1 : selectedIndex + 1;
  const totalOrder = isAdding ? rows.length + 1 : rows.length;

  const handleNavigate = (direction) => {
    const nextIndex = selectedIndex + direction;

    if (nextIndex < 0 || nextIndex >= rows.length) return;

    setSelectedValues(rows[nextIndex]);
  };

  const isActiveOnDay = (row, day) => !row.completed && dayjs(row.plannedDate).isSame(day, 'day');

  const handleDragEnd = async ({ source, destination }) => {
    if (isOrderSaving || !destination || source.index === destination.index) return;

    const sourceRow = rows[source.index];
    const day = sourceRow.plannedDate;

    if (!isActiveOnDay(sourceRow, day) || !isActiveOnDay(rows[destination.index], day)) return;

    const positions = rows
      .filter((row) => isActiveOnDay(row, day))
      .map((row) => row.visOrder)
      .sort((a, b) => a - b);

    const reordered = [...rows];
    reordered.splice(destination.index, 0, ...reordered.splice(source.index, 1));

    const newOrderById = new Map(
      reordered.filter((row) => isActiveOnDay(row, day)).map((row, i) => [row.id, positions[i]])
    );

    const changedRows = reordered
      .filter((row) => newOrderById.has(row.id) && newOrderById.get(row.id) !== row.visOrder)
      .map((row) => ({ id: row.id, visOrder: newOrderById.get(row.id) }));

    if (!changedRows.length) return;

    const updatedRows = reordered
      .map((row) => ({ ...row, visOrder: newOrderById.get(row.id) ?? row.visOrder }))
      .sort((a, b) => dayjs(a.plannedDate).diff(dayjs(b.plannedDate), 'day') || a.visOrder - b.visOrder);

    setRows(updatedRows);
    setIsOrderSaving(true);

    try {
      await materialTransportService.updateOrder(changedRows);
    } catch (error) {
      setRows(rows);
      addNotification({
        content: 'Nem sikerült menteni a fuvarok sorrendjét!',
        type: NotificationType.ERROR,
      });
    } finally {
      setIsOrderSaving(false);
    }
  };

  const FormDirtySync = ({ dirty, onChange }) => {
    useEffect(() => {
      onChange(dirty);
    }, [dirty, onChange]);

    return null;
  };

  const selectTransport = (row) => {
    setIsAdding(false);
    setSelectedValues(row);
    setMobileDetailsOpen(true);
    onCloseConfirmDialog();
  };

  const handleSelectTransport = (row) => {
    if (isAdding && isFormDirty) {
      setConfirmDialogData({
        open: true,
        message: 'A nem mentett adatok elvesznek. Biztosan másik fuvart szeretne kiválasztani?',
        handleChangeElement: selectTransport,
      });
      return;
    }

    selectTransport(row);
  };

  const tabs = [
    {
      id: MaterialTransportView.PENDING,
      name: 'Elvégzendő szállítások',
    },
    {
      id: MaterialTransportView.ALL,
      name: 'Összes szállítás',
    },
  ];

  const handleTabClick = (tabIndex) => {
    setActiveTab(tabs[tabIndex].id);
  };

  return (
    <>
      <Grid container spacing={1} sx={sxConfig.page}>
        <Grid
          item
          xs={12}
          md={3}
          sx={{
            ...sxConfig.gridPanel,
            display: { xs: mobileDetailsOpen ? 'none' : 'block', md: 'block' },
          }}
        >
          <Box sx={[sxConfig.panel, sxConfig.leftPanel]}>
            <Stack sx={sxConfig.transportDateHeader}>
              <Typography sx={[sxConfig.sectionTitle, sxConfig.transportDateTitle]}>Fuvar sorrend</Typography>

              <Stack sx={sxConfig.transportDateActions}>
                <Box sx={sxConfig.transportDatePicker}>
                  <DatePickerUI
                    variant="outlined"
                    size="small"
                    value={selectedDate}
                    onChange={(date) => {
                      setSelectedDate(date);
                    }}
                    clearable={false}
                  />
                </Box>
                <Button
                  startIcon={<EventIcon />}
                  variant="outlined"
                  sx={sxConfig.transportTodayButton}
                  disabled={selectedDate.isSame(dayjs(), 'day')}
                  onClick={() => setSelectedDate(dayjs())}
                >
                  Ma
                </Button>
              </Stack>
            </Stack>
            <TabsUI activeTab={tabs.findIndex((tab) => tab.id === activeTab)} handleTabClick={handleTabClick}>
              {tabs.map((tab) => (
                <div key={tab.id} name={tab.name}>
                  <Stack spacing={1}>
                    {rows.length > 0 ? (
                      <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="material-transports">
                          {(provided) => (
                            <Box ref={provided.innerRef} {...provided.droppableProps}>
                              {rows.map((row, index) => (
                                <Draggable
                                  key={row.id}
                                  draggableId={String(row.id)}
                                  index={index}
                                  isDragDisabled={row.completed || isAdding || isOrderSaving}
                                >
                                  {(provided) => (
                                    <MaterialTransportCard
                                      row={row}
                                      getOperationData={getOperationData}
                                      isSelected={selectedValues.id === row.id}
                                      handleSelect={() => handleSelectTransport(row)}
                                      handleDelete={handleDelete}
                                      dragProvided={provided}
                                      deleteDisabled={isAdding || isOrderSaving}
                                    />
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </Box>
                          )}
                        </Droppable>
                      </DragDropContext>
                    ) : (
                      <Stack alignItems="center" sx={sxConfig.emptyList}>
                        <LocalShippingIcon fontSize="large" />
                        <Typography fontWeight={600} mb={1}>
                          Még nincs felvett fuvar
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </div>
              ))}
            </TabsUI>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<AddIcon />}
              sx={[sxConfig.addButton, sxConfig.addCardButton]}
              disabled={isAdding}
              onClick={handleAddClick}
            >
              Kártya hozzáadása
            </Button>
          </Box>
        </Grid>

        <Grid
          item
          xs={12}
          md={9}
          sx={{
            ...sxConfig.gridPanel,
            display: { xs: mobileDetailsOpen ? 'block' : 'none', md: 'block' },
          }}
        >
          <Box sx={[sxConfig.panel, sxConfig.rightPanel]}>
            {isMobile && mobileDetailsOpen && (
              <Button startIcon={<ArrowBackIcon />} onClick={handleBackToList} sx={sxConfig.backToListButton}>
                Vissza a fuvarokhoz
              </Button>
            )}
            {isAdding || Object.keys(selectedValues).length > 0 ? (
              <Formik
                initialValues={initValues}
                validationSchema={validationSchema}
                validateOnChange={false}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ values, setFieldValue, dirty, resetForm }) => {
                  const findedCompany = autocompleteData?.company?.find((c) => c.id === values.companyId);
                  const { findedOperation, operationColor } = getOperationData(values.operation);

                  const currentVisOrder = rows.find((row) => row.id === values.id)?.visOrder;

                  return (
                    <Form>
                      <FormDirtySync dirty={dirty} onChange={setIsFormDirty} />
                      <Box sx={sxConfig.formContainer}>
                        <Box sx={sxConfig.formContent}>
                          <Stack sx={sxConfig.transportHeader}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={sxConfig.transportHeaderTitle}>
                              <Typography sx={sxConfig.transportTitle}>
                                {currentVisOrder
                                  ? `${currentVisOrder}. ${findedCompany?.name ?? ''}`
                                  : findedCompany?.name ?? ''}
                              </Typography>

                              <Chip
                                label={findedOperation?.label}
                                sx={[sxConfig.operationChip(operationColor), sxConfig.transportHeaderChip]}
                              />
                            </Stack>

                            <Stack direction="row" alignItems="center" spacing={1} sx={sxConfig.transportNavigation}>
                              <Typography sx={sxConfig.transportOrderText}>
                                Sorrend: {currentOrder} / {totalOrder}
                              </Typography>

                              <Stack direction="row" alignItems="center" spacing={1}>
                                <IconButton
                                  size="small"
                                  sx={sxConfig.navigationIconButton}
                                  disabled={selectedIndex <= 0}
                                  onClick={() => handleNavigate(-1)}
                                >
                                  <ArrowBackIosIcon />
                                </IconButton>

                                <IconButton
                                  size="small"
                                  sx={sxConfig.navigationIconButton}
                                  disabled={selectedIndex < 0 || selectedIndex >= rows.length - 1}
                                  onClick={() => handleNavigate(1)}
                                >
                                  <ArrowForwardIosIcon />
                                </IconButton>
                              </Stack>
                            </Stack>
                          </Stack>
                          <MaterialTransportFields
                            selectedValues={selectedValues}
                            autocompleteOptions={autocompleteOptions}
                            findedOperation={findedOperation}
                            operationColor={operationColor}
                          />
                          <Box mt={2}>
                            <MaterialTransportItemTable
                              defTransportItem={defTransportItem}
                              autocompleteOptions={autocompleteOptions}
                            />
                          </Box>
                          <Box mt={2}>
                            <Typography sx={sxConfig.sectionTitle}>Csatolmányok</Typography>
                            <FileUploadUI
                              rootSx={{ m: 0 }}
                              value={values.materialTransportAttachment}
                              onFilesChange={(files) =>
                                setFieldValue(
                                  'materialTransportAttachment',
                                  files.map(({ file }) => file)
                                )
                              }
                            />
                          </Box>
                        </Box>
                        <MaterialTransportActions
                          isAdding={isAdding}
                          selectedIndex={selectedIndex}
                          total={rows.length}
                          handleAddClick={handleAddClick}
                          handleNavigate={handleNavigate}
                          handleClose={() => {
                            resetForm();
                            handleClose();
                          }}
                        />
                      </Box>
                    </Form>
                  );
                }}
              </Formik>
            ) : (
              <Stack alignItems="center" justifyContent="center" sx={sxConfig.emptyState}>
                <AssignmentIcon fontSize="large" />
                <Typography fontWeight={600} mb={1}>
                  Nincs kiválasztott fuvar
                </Typography>
              </Stack>
            )}
          </Box>
        </Grid>
      </Grid>
      <ConfirmDialogUI
        handleChangeElement={confirmDialogData.handleChangeElement}
        show={confirmDialogData.open}
        onHide={() => onCloseConfirmDialog()}
        message={confirmDialogData.message}
      />
    </>
  );
};
